/* eslint-disable @typescript-eslint/no-empty-function */
import { LogApi } from '@Apis';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import {
  ColumnConfigInterface,
  Criterion,
  LogChartParams,
  LogFacetParams,
  LogParams,
  LogState,
  LogTypeEnum,
  SearchItem,
  SortInterface,
  TimeRangeInterface,
} from '@Types/log';
import dayjs from 'dayjs';
import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useReducer,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import config from 'src/config';

import commonUtil from '@Utils/common';
import { renderData } from './utils/contentListHandle';
import {
  convertCriteriaToSearchItems,
  generateContentListParams,
  generateFacetListParams,
} from './utils/logStateHandle';
import {
  getInitState,
  getLogTableConfig,
  getTimeRange,
  LogTimeUnit,
  setLogTableConfig,
} from './shared';

const enum LogActionType {
  SetLogType = 'setLogType',
  SetSortKey = 'setSortKey',
  SetCriteria = 'setCriteria',
  SetFacets = 'setFacets',
  SetLogs = 'setLogs',
  SetLoading = 'setLoading',
  SetColumnConfig = 'setColumnConfig',
  SetPageStart = 'setPageStart',
  SetTimeRange = 'setTimeRange',
  SetQueryParams = 'setQueryParams',
  SetChartData = 'setChartData',
  SetChartLoading = 'setChartLoading',
  SetLoadChart = 'setLoadChart',
}

const useEnhancedReducer = (
  reducer: Parameters<typeof useReducer>[0],
  initState: Parameters<typeof useReducer>[1],
  initializer?: Parameters<typeof useReducer>[2]
): [Parameters<typeof useReducer>[1], Dispatch<any>, Function] => {
  const latestState = useRef<ReturnType<typeof reducer>>(initState);
  const getState = useCallback(() => latestState.current, []);
  return [
    ...useReducer(
      (
        state: Parameters<typeof reducer>[0],
        action: Parameters<typeof reducer>[1]
      ) => (latestState.current = reducer(state, action)),
      initState,
      initializer
    ),
    getState,
  ];
};

const useLogContext = (initState: LogState) => {
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  const abortControllerRef = useRef<AbortController>();
  // just for url click log
  const nextPageRef = useRef<boolean>(true);

  const reducer = (
    preState: LogState,
    action: {
      type: LogActionType;
      payload: Partial<LogState>;
    }
  ): LogState => {
    const { type, payload } = action;
    switch (type) {
      case LogActionType.SetSortKey:
      case LogActionType.SetLogType:
      case LogActionType.SetLogs:
      case LogActionType.SetLoading:
      case LogActionType.SetCriteria:
      case LogActionType.SetColumnConfig:
      case LogActionType.SetPageStart:
      case LogActionType.SetFacets:
      case LogActionType.SetTimeRange:
      case LogActionType.SetQueryParams:
      case LogActionType.SetChartData:
      case LogActionType.SetChartLoading:
      case LogActionType.SetLoadChart:
        return {
          ...preState,
          ...payload,
        };
      default:
        commonUtil.neverValueGuard(type);
        return preState;
    }
  };
  const [state, dispatch, getState] = useEnhancedReducer(
    reducer,
    initState
  ) as [
    LogState,
    React.Dispatch<{ type: LogActionType; payload: Partial<LogState> }>,
    () => LogState,
  ];
  const setLogType = useCallback((logType: LogTypeEnum) => {
    localStorage.setItem('casLogType', logType);
    //logType can be set to the same then columnConfig reset to empty, so exclude columnConfig here,
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { columnConfig, ...defaultState } =
      config.ui.logDefaultValue.logState;
    const curState = {
      ...defaultState,
      type: logType,
      timeRange: getTimeRange(LogTimeUnit.Last24h, logType),
      page: getInitPage(logType),
    };
    dispatch({
      type: LogActionType.SetLogType,
      payload: curState,
    });
    queryLogs(curState as any);
  }, []);

  const setSortKey = (sort: SortInterface) => {
    const { type } = getState();
    window.document
      .querySelector('.logTableContainer > div')
      ?.scrollTo({ top: 0 });
    const payload = {
      page: getInitPage(type),
      sort,
    };

    dispatch({
      type: LogActionType.SetSortKey,
      payload,
    });
    queryLogs({
      ...getState(),
      ...payload,
    });
  };
  const setFacets = useCallback((facets: SearchItem[]) => {
    const { type } = getState();
    window.document
      .querySelector('.logTableContainer > div')
      ?.scrollTo({ top: 0 });

    const payload = {
      page: getInitPage(type),
      facets,
    };

    dispatch({
      type: LogActionType.SetFacets,
      payload,
    });
    // set load chart anytime facets change, in chart, it will check if the chart is opened, if chart is opened, load the chart data
    setLoadChart(new Date().getTime().toString());
    queryLogs({
      ...getState(),
      ...payload,
    });
  }, []);
  const setCriteria = useCallback((criteria: Criterion[]) => {
    dispatch({
      type: LogActionType.SetCriteria,
      payload: {
        criteria,
      },
    });
    console.log('Context: set criteria, criteria:', criteria);
  }, []);
  const setColumnConfig = useCallback((columnConfig: ColumnConfigInterface) => {
    const { type } = getState();
    const casLogTableConfig = getLogTableConfig();
    setLogTableConfig({
      ...casLogTableConfig,
      [type]: columnConfig,
    });

    dispatch({
      type: LogActionType.SetColumnConfig,
      payload: {
        columnConfig,
      },
    });
  }, []);
  const setPageStart = useCallback((start: number) => {
    const { type } = getState();
    const page = {
      ...getInitPage(type),
      start,
    };
    dispatch({
      type: LogActionType.SetPageStart,
      payload: {
        page,
      },
    });

    if (start === 1) {
      return;
    }
    const state = {
      ...getState(),
      page,
    };
    const scrollTop =
      window.document.querySelector('.logTableContainer > div')?.scrollTop || 0;
    const fetchData = async () => {
      const param = generateContentListParams(state);
      try {
        setLoading(true);
        const res = await LogApi.getRawLog(param);
        if (
          res.data.contentList.length === 0 &&
          state.type === LogTypeEnum.URLTRACKING
        ) {
          nextPageRef.current = false;
        }
        setLogs(
          state.logs.concat(renderData(res.data.contentList, state.type))
        );
      } catch (error) {
        console.error('get next page log error, ', JSON.stringify(error));
        window.showNotification({
          titleMsg: t('errors.log.search.title'),
          bodyMsg: t('errors.log.search.body'),
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData().then();
    window.document
      .querySelector('.logTableContainer > div')
      ?.scrollTo({ top: scrollTop });
  }, []);
  const setLoading = useCallback((isLoading: boolean) => {
    dispatch({
      type: LogActionType.SetLoading,
      payload: {
        isLoading,
      },
    });
  }, []);
  const setFacetLoading = useCallback((isFacetLoading: boolean) => {
    dispatch({
      type: LogActionType.SetLoading,
      payload: {
        isFacetLoading,
      },
    });
  }, []);
  const setChartLoading = useCallback((isChartLoading: boolean) => {
    dispatch({
      type: LogActionType.SetLoading,
      payload: {
        isChartLoading,
      },
    });
  }, []);
  const setLogs = useCallback((logs: any[]) => {
    dispatch({
      type: LogActionType.SetLogs,
      payload: {
        logs,
      },
    });
  }, []);
  const setTimeRange = useCallback((timeRange: TimeRangeInterface) => {
    dispatch({
      type: LogActionType.SetTimeRange,
      payload: {
        timeRange,
      },
    });
  }, []);
  const setChartData = useCallback((chartData: any) => {
    dispatch({
      type: LogActionType.SetChartData,
      payload: {
        chartData,
      },
    });
  }, []);
  const getChartData = useCallback(
    async (timeRange: TimeRangeInterface, interval: string) => {
      const state = getState();
      setChartLoading(true);
      try {
        const convertedCriteria: SearchItem[] =
          convertCriteriaToSearchItems(state);
        const logchartParams: LogChartParams = {
          type: state.type,
          timeRange,
          interval,
          searches: JSON.stringify([...convertedCriteria, ...state.facets]),
        };
        const dateFormat = t('common.momentDateFormat');
        const shortDateFormat = t('common.momentShortDateFormat');
        const monthDateFormat = t('common.momentMonthDateFormat');
        const res = await LogApi.getLogChartData(logchartParams);
        let chartData = res.data;
        const format =
          interval === '1h'
            ? dateFormat
            : interval === '1M'
              ? monthDateFormat
              : shortDateFormat;
        chartData = chartData.map(([time, count]) => {
          return [dayjs(time).utcOffset(offset).format(format), count];
        });
        setChartData(chartData);
      } catch (error) {
        console.error('getChartData error: ' + error);
        window.showNotification({
          titleMsg: t('errors.log.search.title'),
          bodyMsg: t('errors.log.search.body'),
          severity: 'error',
        });
      } finally {
        setChartLoading(false);
      }
    },
    []
  );
  const setQueryParams = useCallback(
    (
      type: LogTypeEnum,
      timeRange: TimeRangeInterface,
      facets: SearchItem[],
      criteria: Criterion[]
    ) => {
      const payload = { type, timeRange, facets, criteria };
      dispatch({
        type: LogActionType.SetQueryParams,
        payload,
      });
      localStorage.setItem('casLogType', type);
      const curState = {
        ...getState(),
        ...payload,
      };

      queryLogs(curState);
    },
    []
  );
  const setLoadChart = useCallback((loadChart: string) => {
    dispatch({
      type: LogActionType.SetLoadChart,
      payload: {
        loadChart,
      },
    });
  }, []);

  const getInitPage = useCallback((type: LogTypeEnum) => {
    return {
      start: 1,
      size: type === LogTypeEnum.URLTRACKING ? 20 : 100,
    };
  }, []);

  const hasNextPage = useCallback(() => {
    return nextPageRef.current;
  }, []);

  const queryLogs = useCallback(async (state: LogState) => {
    // when full query, init nextPageRef
    nextPageRef.current = true;
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const logParams: LogParams = generateContentListParams(state);
    const facetParams: LogFacetParams = generateFacetListParams(state);

    const logType = state.type;

    async function getContentLogs() {
      setLoading(true);
      const resLog = (
        await LogApi.getRawLog(logParams, {
          signal: abortControllerRef.current?.signal,
        })
      ).data;
      dispatch({
        type: LogActionType.SetLogs,
        payload: {
          logs: renderData(resLog.contentList, logType),
          total: resLog.total,
          page: getInitPage(logType),
        },
      });
      setLoading(false);
    }

    async function getFacetLogs() {
      setFacetLoading(true);
      const resFacet = await LogApi.getRawFacets(facetParams, {
        signal: abortControllerRef.current?.signal,
      });
      dispatch({
        type: LogActionType.SetLogs,
        payload: {
          allFacets: resFacet.data,
        },
      });
      setFacetLoading(false);
    }

    try {
      await getContentLogs();
      if (logType !== 'urlTracking') {
        await getFacetLogs();
      }
    } catch (error) {
      if ((error as any).name === 'CanceledError') {
        console.error(`getLogs request aborted: ${error}`);
        return;
      }
      console.error('getLogs error: ' + JSON.stringify(error));
      window.showNotification({
        titleMsg: t('errors.log.search.title'),
        bodyMsg: t('errors.log.search.body'),
        severity: 'error',
      });
      setFacetLoading(false);
      setLoading(false);
    }
  }, []);

  return {
    state,
    getState,
    setLogType,
    setSortKey,
    setFacets,
    setCriteria,
    setColumnConfig,
    setPageStart,
    setLoading,
    setTimeRange,
    setQueryParams,
    setChartData,
    getChartData,
    setChartLoading,
    setLoadChart,
    hasNextPage,
    queryLogs,
  } as {
    state: LogState;
    getState: () => LogState;
    setLogType: typeof setLogType;
    setSortKey: typeof setSortKey;
    setFacets: typeof setFacets;
    setCriteria: typeof setCriteria;
    setColumnConfig: typeof setColumnConfig;
    setPageStart: typeof setPageStart;
    setLoading: typeof setLoading;
    setTimeRange: typeof setTimeRange;
    setQueryParams: typeof setQueryParams;
    setChartData: typeof setChartData;
    getChartData: typeof getChartData;
    setChartLoading: typeof setChartLoading;
    setLoadChart: typeof setLoadChart;
    hasNextPage: () => boolean;
    queryLogs: typeof queryLogs;
  };
};
type UseLogContextType = ReturnType<typeof useLogContext>;
const LogContext = createContext<UseLogContextType>({
  state: getInitState() as LogState,
  getState: getInitState,
  setLogType: () => {},
  setSortKey: () => {},
  setFacets: () => {},
  setCriteria: () => {},
  setColumnConfig: () => {},
  setPageStart: () => {},
  setLoading: () => {},
  setTimeRange: () => {},
  setQueryParams: () => {},
  setChartData: () => {},
  getChartData: async () => {},
  setChartLoading: () => {},
  setLoadChart: () => {},
  hasNextPage: () => true,
  queryLogs: async () => {},
});
export const LogContextProvider: React.FC<{ children: React.ReactNode }> = (
  props
) => {
  const initState = getInitState();

  return (
    <LogContext.Provider value={useLogContext(initState)}>
      {props.children}
    </LogContext.Provider>
  );
};

export const useLogReducerContext = () => {
  return useContext(LogContext);
};
